import * as React from "react";
import { FaSignInAlt, FaSignOutAlt } from "react-icons/fa";
import { IconButton, IconButtonProps } from "@chakra-ui/react";
import { LOGIN_URL, USER_POOL_CLIENT_ID, USER_POOL_ID } from "../environment";
import { useEffect, useState } from "react";
import { Buffer } from "buffer";
import { CognitoJwtVerifier } from "aws-jwt-verify";

type UserLoginIndicatorProps = Omit<IconButtonProps, "aria-label">;

/**
 * The user login indicator.
 *
 * @class
 * @param {UserLoginIndicatorProps} props The properties.
 * @augments {React.FC<UserLoginIndicatorProps>}
 */
export const UserLoginIndicator: React.FC<UserLoginIndicatorProps> = (
  props,
) => 
{
  const [isAuthenticated, setAuthenticated] = useState(false);

  console.debug("[UserLoginIndicator]");

  /**
   * Log in to mumiver.se.
   */
  function login() 
  {
    console.debug("[UserLoginIndicator] login");

    window.location.href = `${LOGIN_URL}/?redirectUrl=${window.location.protocol}//${window.location.host}/`;
  }

  /**
   * Log out from mumiver.se.
   */
  function logout() 
  {
    console.debug("[UserLoginIndicator] logout");

    window.location.href = `${LOGIN_URL}/logout?redirectUrl=${window.location.protocol}//${window.location.host}/`;
  }

  useEffect(() => 
  {
    /**
     * Verify the tokens.
     *
     * @param {string} accessToken The access token.
     * @param {string} idToken The id token.
     * @param {string} refreshToken The refresh token.
     * @returns {Promise<void>}
     */
    async function verifyToken(
      accessToken: string,
      idToken: string | null,
      refreshToken: string | null,
    ) 
    {
      if (!isAuthenticated) 
      {
        const expirationDate = window.sessionStorage.getItem("expiration");

        if (
          expirationDate !== null
          && parseInt(expirationDate) * 1000 <= Date.now()
        ) 
        {
          console.warn(
            "[UserLoginIndicator] Expiration date has been exceeded. Set user as unauthenticated.",
          );

          window.sessionStorage.removeItem("expiration");
          window.sessionStorage.removeItem("username");

          setAuthenticated(false);
        }

        try 
        {
          const verifier = CognitoJwtVerifier.create({
            userPoolId: USER_POOL_ID,
            tokenUse: "access",
            clientId: USER_POOL_CLIENT_ID,
          });

          const parsedToken = JSON.parse(
            Buffer.from(accessToken, "base64").toString(),
          );

          const payload = await verifier.verify(parsedToken.jwtToken);

          console.debug("[UserLoginIndicator] Verified access token", {
            accessToken: accessToken,
            payload: payload,
            serializedPayload: JSON.stringify(payload),
          });

          if (payload.exp && payload.exp * 1000 >= Date.now()) 
          {
            window.sessionStorage.setItem("expiration", payload.exp.toString());

            if (idToken !== null) 
            {
              const identityVerifier = CognitoJwtVerifier.create({
                userPoolId: USER_POOL_ID,
                tokenUse: "id",
                clientId: USER_POOL_CLIENT_ID,
              });

              const idJwtToken = JSON.parse(
                Buffer.from(idToken, "base64").toString(),
              ).jwtToken;

              const identityPayload = await identityVerifier.verify(idJwtToken);

              console.debug("[UserLoginIndicator] Verified id token", {
                idToken: idJwtToken,
                payload: identityPayload,
                serializedPayload: JSON.stringify(identityPayload),
              });

              if (identityPayload["email"]) 
              {
                window.sessionStorage.setItem(
                  "username",
                  identityPayload["email"].toString(),
                );
                window.sessionStorage.setItem("userToken", idJwtToken);

                if (refreshToken !== null) 
                {
                  window.sessionStorage.setItem(
                    "refreshToken",
                    JSON.parse(Buffer.from(refreshToken, "base64").toString())
                      .jwtToken,
                  );
                }

                setAuthenticated(true);
              }
            }
          }
        }
        catch (error: unknown) 
        {
          console.error(error);

          return null;
        }
      }
    }

    const userName = window.sessionStorage.getItem("username");
    const expirationDate = window.sessionStorage.getItem("expiration");

    const urlQueryParameters = new URLSearchParams(window.location.search);

    const accessToken = urlQueryParameters.get("accessToken");

    if (accessToken !== null) 
    {
      console.debug("[UserLoginIndicator] access token found", {
        userName: userName,
        expirationDate: expirationDate,
        accessToken: accessToken,
      });

      window.sessionStorage.removeItem("userName");
      window.sessionStorage.removeItem("expirationDate");
      window.sessionStorage.removeItem("userToken");
      window.sessionStorage.removeItem("refreshToken");

      verifyToken(
        accessToken,
        urlQueryParameters.get("idToken"),
        urlQueryParameters.get("refreshToken"),
      );
    }
    else if (!isAuthenticated) 
    {
      console.debug("[UserLoginIndicator] unauthenticated", {
        userName: userName,
        expirationDate: parseInt(expirationDate ?? "0") * 1000,
        date: Date.now(),
      });

      const userToken = window.sessionStorage.getItem("userToken");
      const refreshToken = window.sessionStorage.getItem("refreshToken");

      if (
        userToken !== null
        && refreshToken !== null
        && userName !== null
        && expirationDate !== null
        && parseInt(expirationDate) * 1000 >= Date.now()
      ) 
      {
        console.debug(
          "[UserLoginIndicator] found authentication data in session storage. Set as authenticated.",
        );

        setAuthenticated(true);
      }
    }
  }, [isAuthenticated]);

  const SwitchIcon = isAuthenticated ? FaSignOutAlt : FaSignInAlt;

  const text = isAuthenticated ? "Sign Out" : "Sign In";

  return (
    <IconButton
      size="md"
      fontSize="lg"
      variant="ghost"
      color="current"
      marginLeft="2"
      onClick={(event) => (isAuthenticated ? logout() : login())}
      icon={<SwitchIcon />}
      aria-label={text}
      title={text}
      {...props}
    />
  );
};